import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import LineChart, { COLORS } from './LineChart';
import { fmt, periodLabel, downloadCsv } from './format';

/** /data-hub/{key} - one dataset: country trends (chart + latest) or places (ranked bars + sortable table). */
export default class DatasetView extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.d = null;
    this.error = null;
    this.hidden = {};
    this.query = '';
    this.sort = 'rank';
    app.request({
      method: 'GET',
      url: app.forum.attribute('apiUrl') + '/datahub/datasets/' + encodeURIComponent(this.attrs.datasetKey),
      errorHandler: () => {}
    }).then(r => {
      this.d = r;
      if (r.name) app.setTitle(r.name);
      // Population pages: also Nigerian-born people abroad and Nigeria by age group
      if (this.isPopulation(r)) {
        app.request({
          method: 'GET',
          url: app.forum.attribute('apiUrl') + '/datahub/population-overview',
          errorHandler: () => {}
        }).then(o => {
          this.overview = o;
          m.redraw();
        }).catch(() => {});
      }
    }).catch(() => this.error = 'This dataset was not found.').finally(() => m.redraw());
  }
  view() {
    if (this.error) return m("p", {
      className: "DataHubPub-muted"
    }, this.error);
    if (!this.d) return m(LoadingIndicator, null);
    const d = this.d;
    return m("div", {
      className: "DataHubPub"
    }, m("a", {
      className: "DataHubPub-back",
      href: app.route('voucha-datahub.index')
    }, m("i", {
      className: "fas fa-arrow-left"
    }), " All data"), m("h1", {
      className: "DataHubPub-title"
    }, d.name), app.vouchaPageText ? app.vouchaPageText('/data-hub/' + d.key, 'DataHubPub-summary') : null, d.notes ? m("p", {
      className: "DataHubPub-muted"
    }, d.notes) : null, d.latestOnly ? m("p", {
      className: "DataHubPub-muted"
    }, "No figures published since January ", d.fromYear, "; showing the latest available.") : null, d.kind === 'series' ? this.series(d) : d.kind === 'list' ? this.list(d) : this.isPopulation(d) ? this.population(d) : this.places(d), m("p", {
      className: "DataHubPub-source"
    }, "Source:", ' ', d.sourceUrl ? m("a", {
      href: d.sourceUrl,
      target: "_blank",
      rel: "noopener"
    }, d.sourceName || d.sourceUrl) : d.sourceName || '—', d.period ? ' · ' + d.period : '', d.updated ? ' · updated ' + new Date(d.updated.replace(' ', 'T') + 'Z').toLocaleDateString('en-GB', {
      dateStyle: 'medium'
    }) : ''));
  }

  /** Tables show 10 rows, then "Show all N" (searching shows every match; CSV downloads keep all rows) */
  capped(key, rows) {
    this.expanded = this.expanded || {};
    return this.expanded[key] || rows.length <= 10 ? rows : rows.slice(0, 10);
  }
  more(key, total) {
    if (total <= 10) return null;
    const open = this.expanded && this.expanded[key];
    return m("div", {
      className: "DataHubPub-more"
    }, m("button", {
      className: "Button",
      onclick: () => this.expanded[key] = !open
    }, open ? 'Show fewer' : 'Show all ' + total), !open ? m("span", {
      className: "DataHubPub-muted"
    }, " or search above") : null);
  }
  series(d) {
    // one figure per country (no trend yet): a line chart is just scattered dots, so rank them as bars
    if (!d.series.some(s => s.points.length > 1)) return this.snapshot(d);
    return m("div", null, m("div", {
      className: "DataHubPub-legend"
    }, d.series.map((s, i) => m("button", {
      className: 'DataHubPub-key' + (this.hidden[s.id] ? ' is-off' : ''),
      onclick: () => this.hidden[s.id] = !this.hidden[s.id]
    }, m("span", {
      style: {
        background: COLORS[i % COLORS.length]
      }
    }), " ", s.name))), m(LineChart, {
      series: d.series,
      unit: d.unit,
      hidden: this.hidden
    }), m("table", {
      className: "DataHubPub-table"
    }, m("thead", null, m("tr", null, m("th", null, d.seriesLabel || 'Country'), m("th", null, "Latest"), m("th", null, "Period"), m("th", null, "Change"))), m("tbody", null, this.capped('series', d.series).map(s => {
      const last = s.points[s.points.length - 1];
      const prev = s.points[s.points.length - 2];
      const ch = last && prev ? Math.round((last.value - prev.value) * 100) / 100 : null;
      return m("tr", null, m("td", null, s.name), m("td", null, m("strong", null, last ? fmt(last.value, d.unit) : '—')), m("td", null, last ? periodLabel(last.period) : '—'), m("td", {
        className: ch > 0 ? 'is-up' : ch < 0 ? 'is-down' : ''
      }, ch === null ? '—' : ch === 0 ? 'unchanged' : (ch > 0 ? '▲ +' : '▼ ') + Math.abs(ch) + (d.unit === '%' ? ' pts' : '')));
    }))), this.more('series', d.series.length), m("button", {
      className: "Button DataHubPub-csv",
      onclick: () => downloadCsv(d.key + '.csv', [d.seriesLabel || 'Country', 'Period', d.name + (d.unit ? ' (' + d.unit + ')' : '')], d.series.flatMap(s => s.points.map(p => [s.name, p.period, p.value])))
    }, m("i", {
      className: "fas fa-download"
    }), " Download CSV"));
  }

  /** A series dataset where each country has a single figure: ranked bars + latest table */
  snapshot(d) {
    const rows = d.series.map(s => ({
      name: s.name,
      last: s.points[s.points.length - 1]
    })).filter(r => r.last).sort((a, b) => b.last.value - a.last.value);
    const max = Math.max(...rows.map(r => Math.abs(r.last.value)), 1);
    return m("div", null, m("div", {
      className: "DataHubPub-bars"
    }, rows.map((r, i) => this.bar('#' + (i + 1), r.name, Math.abs(r.last.value), max, fmt(r.last.value, d.unit), '(' + periodLabel(r.last.period) + ')'))), m("button", {
      className: "Button DataHubPub-csv",
      onclick: () => downloadCsv(d.key + '.csv', [d.seriesLabel || 'Country', 'Period', d.name + (d.unit ? ' (' + d.unit + ')' : '')], d.series.flatMap(s => s.points.map(p => [s.name, p.period, p.value])))
    }, m("i", {
      className: "fas fa-download"
    }), " Download CSV"));
  }
  list(d) {
    const max = Math.max(...d.rows.map(r => r.value || 0), 1);
    return m("div", null, m("div", {
      className: "DataHubPub-bars"
    }, this.capped('list', d.rows).map(r => m("div", {
      className: "DataHubPub-bar"
    }, m("span", {
      className: "DataHubPub-barRank"
    }, "#", r.rank), m("span", {
      className: "DataHubPub-barName"
    }, r.source ? m("a", {
      href: r.source,
      target: "_blank",
      rel: "noopener",
      title: "Source"
    }, r.name) : r.name), m("span", {
      className: "DataHubPub-barTrack"
    }, m("span", {
      style: {
        width: Math.max(1, (r.value || 0) / max * 100) + '%'
      }
    })), m("span", {
      className: "DataHubPub-barValue"
    }, r.value === null ? '—' : Math.round(r.value).toLocaleString('en-US'), r.period ? m("small", null, " (", r.period, ")") : null)))), this.more('list', d.rows.length), m("button", {
      className: "Button DataHubPub-csv",
      onclick: () => downloadCsv(d.key + '.csv', ['Rank', 'Name', d.name + (d.unit ? ' (' + d.unit + ')' : ''), 'Year', 'Source'], d.rows.map(r => [r.rank, r.name, r.value, r.period, r.source]))
    }, m("i", {
      className: "fas fa-download"
    }), " Download CSV"));
  }
  isPopulation(d) {
    return d && d.kind === 'places' && d.level !== 'lga' && /^population_/.test(d.key || '');
  }

  /** A horizontal bar row */
  bar(rank, name, value, max, label, extra) {
    return m("div", {
      className: "DataHubPub-bar"
    }, m("span", {
      className: "DataHubPub-barRank"
    }, rank), m("span", {
      className: "DataHubPub-barName"
    }, name), m("span", {
      className: "DataHubPub-barTrack"
    }, m("span", {
      style: {
        width: Math.max(1, value / max * 100) + '%'
      }
    })), m("span", {
      className: "DataHubPub-barValue"
    }, label, extra ? m("small", {
      className: "DataHubPub-barShare"
    }, " ", extra) : null));
  }

  /** Population pages: top 5 states (search for the rest), Nigerian-born abroad, Nigeria by age group */
  population(d) {
    const total = d.rows.reduce((sum, r) => sum + r.value, 0);
    const share = v => total > 0 ? (100 * v / total).toFixed(1) + '%' : '';
    const q = this.query.trim().toLowerCase();
    const found = q ? d.rows.filter(r => [r.name, r.zone].join(' ').toLowerCase().includes(q)) : d.rows.slice(0, 5);
    const max = Math.max(...d.rows.map(r => r.value), 1);
    const people = v => v >= 1e6 ? (v / 1e6).toFixed(1) + 'M' : Math.round(v).toLocaleString('en-US');
    const o = this.overview || {};
    const abroad = o.abroad || [];
    const ages = o.ages;
    return m("div", {
      className: "DataHubPop"
    }, m("section", {
      className: "DataHubPop-section"
    }, m("h3", null, q ? 'States matching “' + this.query.trim() + '”' : 'Most populated states'), m("div", {
      className: "DataHubPub-search"
    }, m("i", {
      className: "fas fa-search"
    }), m("input", {
      className: "FormControl",
      placeholder: "Find any state or region",
      value: this.query,
      oninput: e => this.query = e.target.value
    })), m("div", {
      className: "DataHubPub-bars"
    }, found.map(r => this.bar('#' + r.rank, r.name, r.value, max, people(r.value), share(r.value) + ' of Nigeria')), !found.length ? m("p", {
      className: "DataHubPub-muted"
    }, "No state matches.") : null), !q ? m("p", {
      className: "DataHubPub-muted DataHubPop-hint"
    }, d.rows.length - 5, " more states: search above, or download them all.") : null), abroad.length ? m("section", {
      className: "DataHubPop-section"
    }, m("h3", null, "Where Nigerian-born people live abroad"), m("div", {
      className: "DataHubPub-bars"
    }, abroad.map((c, i) => this.bar('#' + (i + 1), c.name, c.value, abroad[0].value, people(c.value), '(' + c.period + ')'))), m("p", {
      className: "DataHubPub-muted DataHubPop-hint"
    }, "People born in Nigeria, from each country's latest census or survey. ", m("a", {
      href: "/data-hub/nigerians_abroad"
    }, "All figures \u2192"))) : null, ages && ages.groups.length ? m("section", {
      className: "DataHubPop-section"
    }, m("h3", null, "Nigeria by generation (", ages.year, ")"), m("div", {
      className: "DataHubPub-bars"
    }, ages.groups.map(g => this.bar('', g.label, g.value, Math.max(...ages.groups.map(x => x.value), 1), people(g.value), (g.share !== null ? g.share + '%' : '') + ' · born ' + g.born))), ages.extra.length ? m("div", {
      className: "DataHubPop-facts"
    }, ages.extra.map(x => m("div", {
      className: "DataHubPop-fact"
    }, m("strong", null, people(x.value)), m("span", null, x.label, x.share !== null ? ' · ' + x.share + '%' : '')))) : null, m("p", {
      className: "DataHubPub-muted DataHubPop-hint"
    }, "Total ", people(ages.total), " people. ", ages.source, ".")) : null, m("button", {
      className: "Button DataHubPub-csv",
      onclick: () => downloadCsv(d.key + '.csv', ['Rank', 'State', 'Region', d.name + (d.unit ? ' (' + d.unit + ')' : '')], d.rows.map(r => [r.rank, r.name, r.zone, r.value]))
    }, m("i", {
      className: "fas fa-download"
    }), " Download all states (CSV)"));
  }
  places(d) {
    const q = this.query.trim().toLowerCase();
    let rows = d.rows.filter(r => !q || [r.name, r.state, r.zone].join(' ').toLowerCase().includes(q));
    rows = rows.slice().sort((a, b) => this.sort === 'name' ? a.name.localeCompare(b.name) : a.rank - b.rank);
    const all = rows.length;
    if (!q) rows = this.capped('places', rows);
    const max = Math.max(...d.rows.map(r => r.value), 1);
    // Context the bars don't show: population, the figure per million people (for counts), and IGR
    const ctx = d.context || {};
    const perMillion = !!ctx.population && !/[%₦$]|bn|trn|rate|index/i.test(d.unit || '');
    const thLink = (key, label) => m("th", null, m("a", {
      href: "#",
      onclick: e => {
        e.preventDefault();
        this.sort = key;
      },
      className: this.sort === key ? 'is-sorted' : ''
    }, label));
    return m("div", null, m("div", {
      className: "DataHubPub-search"
    }, m("i", {
      className: "fas fa-search"
    }), m("input", {
      className: "FormControl",
      placeholder: "Search by state or region",
      value: this.query,
      oninput: e => this.query = e.target.value
    })), m("div", {
      className: "DataHubPub-bars"
    }, rows.map(r => m("div", {
      className: "DataHubPub-bar"
    }, m("span", {
      className: "DataHubPub-barRank"
    }, "#", r.rank), m("span", {
      className: "DataHubPub-barName"
    }, r.name, r.state ? ', ' + r.state : ''), m("span", {
      className: "DataHubPub-barTrack"
    }, m("span", {
      style: {
        width: Math.max(1, r.value / max * 100) + '%'
      }
    })), m("span", {
      className: "DataHubPub-barValue"
    }, fmt(r.value, d.unit)))), !rows.length ? m("p", {
      className: "DataHubPub-muted"
    }, "No matches.") : null), !q ? this.more('places', all) : null, m("table", {
      className: "DataHubPub-table"
    }, m("thead", null, m("tr", null, thLink('rank', 'Rank'), thLink('name', d.level === 'lga' ? 'LGA' : 'State'), d.level === 'lga' ? m("th", null, "State") : m("th", null, "Region"), m("th", null, d.name), ctx.population ? m("th", null, "Population") : null, perMillion ? m("th", null, "Per million people") : null, ctx.igr ? m("th", null, "IGR (\u20A6 bn)") : null)), m("tbody", null, rows.map(r => m("tr", null, m("td", null, r.rank), m("td", null, d.level !== 'lga' && r.slug ? m("a", {
      className: "DHStateLink",
      href: '/data-hub/states/' + r.slug,
      onclick: e => {
        if (e.ctrlKey || e.metaKey) return;
        e.preventDefault();
        m.route.set('/data-hub/states/' + r.slug);
      }
    }, r.name) : r.name), m("td", null, d.level === 'lga' ? r.state : r.zone), m("td", null, m("strong", null, fmt(r.value, d.unit === 'people' ? '' : d.unit))), ctx.population ? m("td", null, r.population ? Math.round(r.population).toLocaleString('en-US') : '—') : null, perMillion ? m("td", null, r.population ? (r.value / r.population * 1e6).toFixed(1) : '—') : null, ctx.igr ? m("td", null, r.igr ? Math.round(r.igr).toLocaleString('en-US') : '—') : null)))), !q ? this.more('places', all) : null, m("button", {
      className: "Button DataHubPub-csv",
      onclick: () => downloadCsv(d.key + '.csv', ['Rank', d.level === 'lga' ? 'LGA' : 'State', d.level === 'lga' ? 'State' : 'Region', d.name + (d.unit ? ' (' + d.unit + ')' : '')], d.rows.map(r => [r.rank, r.name, d.level === 'lga' ? r.state : r.zone, r.value]))
    }, m("i", {
      className: "fas fa-download"
    }), " Download CSV"));
  }
}